import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Checkbox,
  Typography,
  Paper,
  Box,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "./EditPage";
import { handleError, compressedBase64ToString } from "../../utils/lib";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactoryIcon from '@mui/icons-material/Factory';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useTranslation } from 'react-i18next';

// Memoized Product Item Component
const ProductItem = React.memo(({ product, isSelected, onToggle }) => {
  const handleCheckboxChange = useCallback(() => {
    onToggle(product.id);
  }, [product.id, onToggle]);

  const handleCheckboxClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <ListItem
      secondaryAction={
        <Checkbox
          checked={isSelected}
          onChange={handleCheckboxChange}
          onClick={handleCheckboxClick}
        />
      }
      sx={{ 
        pl: 2,
        pr: 5,
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <ListItemAvatar sx={{ minWidth: 44 }}>
        <Avatar
          src={product.thumb_url || undefined}
          alt={product.name}
          variant="rounded"
          sx={{ width: 32, height: 32 }}
        >
          <InventoryIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={product.name}
        secondary={
          product.categories?.map(c => c.name).join(', ') || ''
        }
        sx={{ minWidth: 0, overflow: 'hidden' }}
        primaryTypographyProps={{ noWrap: true }}
        secondaryTypographyProps={{ noWrap: true }}
      />
    </ListItem>
  );
});

ProductItem.displayName = 'ProductItem';

// Memoized Manufacturer Accordion Component
const ManufacturerAccordion = React.memo(({ 
  manufacturer, 
  isExpanded, 
  selectedProducts,
  onAccordionChange,
  onManufacturerToggle,
  onProductToggle,
  isLastItem
}) => {
  const { t } = useTranslation();
  const productIds = useMemo(() => 
    manufacturer.products?.map(p => p.id) || [], 
    [manufacturer.products]
  );

  const selectionStats = useMemo(() => {
    const products = manufacturer.products || [];
    if (products.length === 0) {
      return { selectedCount: 0, totalCount: 0, isFullySelected: false, isPartiallySelected: false };
    }
    
    const selectedCount = products.filter(p => selectedProducts.has(p.id)).length;
    const totalCount = products.length;
    const isFullySelected = selectedCount === totalCount;
    const isPartiallySelected = selectedCount > 0 && selectedCount < totalCount;
    
    return { selectedCount, totalCount, isFullySelected, isPartiallySelected };
  }, [manufacturer.products, selectedProducts]);

  const handleManufacturerToggleClick = useCallback((e) => {
    e.stopPropagation();
    onManufacturerToggle(manufacturer, productIds, selectionStats.isFullySelected);
  }, [manufacturer, productIds, selectionStats.isFullySelected, onManufacturerToggle]);

  const handleAccordionChangeLocal = useCallback((event, isExpanded) => {
    onAccordionChange(manufacturer.id, isExpanded);
  }, [manufacturer.id, onAccordionChange]);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleAccordionChangeLocal}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        borderBottom: !isLastItem ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          minHeight: 64,
          overflow: 'hidden',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            overflow: 'hidden',
            my: 0,
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            my: 0,
          },
        }}
      >
        <Avatar
          src={manufacturer.thumb_url || undefined}
          alt={manufacturer.name}
          sx={{ width: 40, height: 40 }}
        >
          <FactoryIcon />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="subtitle1" noWrap>
            {manufacturer.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {manufacturer.web_page || ''}
          </Typography>
          <Typography variant="caption" color="primary">
            {t('edit.selectedCount', { selected: selectionStats.selectedCount, total: selectionStats.totalCount })}
          </Typography>
        </Box>

        <IconButton
          onClick={handleManufacturerToggleClick}
          color={selectionStats.isFullySelected ? "primary" : "default"}
        >
          {selectionStats.isFullySelected ? (
            <CheckBoxIcon />
          ) : selectionStats.isPartiallySelected ? (
            <CheckBoxIcon color="action" />
          ) : (
            <CheckBoxOutlineBlankIcon />
          )}
        </IconButton>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {manufacturer.products?.map((product, productIndex) => (
            <React.Fragment key={product.id}>
              {productIndex > 0 && <Divider variant="inset" component="li" />}
              <ProductItem
                product={product}
                isSelected={selectedProducts.has(product.id)}
                onToggle={onProductToggle}
              />
            </React.Fragment>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if:
  // 1. manufacturer changed
  // 2. isExpanded changed
  // 3. isLastItem changed
  // 4. Any product in this manufacturer changed its selection state
  
  if (prevProps.manufacturer.id !== nextProps.manufacturer.id) return false;
  if (prevProps.isExpanded !== nextProps.isExpanded) return false;
  if (prevProps.isLastItem !== nextProps.isLastItem) return false;
  
  // Check if any product selection state changed for this manufacturer
  const prevProducts = prevProps.manufacturer.products || [];
  const nextProducts = nextProps.manufacturer.products || [];
  
  if (prevProducts.length !== nextProducts.length) return false;
  
  for (const product of prevProducts) {
    const wasSelected = prevProps.selectedProducts.has(product.id);
    const isSelected = nextProps.selectedProducts.has(product.id);
    if (wasSelected !== isSelected) return false;
  }
  
  // All checks passed, props are equal, don't re-render
  return true;
});

ManufacturerAccordion.displayName = 'ManufacturerAccordion';

const EditProductAssoc = () => {
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [expandedManufacturers, setExpandedManufacturers] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { t } = useTranslation();

  useEffect(() => {
    updateTitle('', t('main.associateProducts'), t('edit.productsSelected', { count: selectedProducts.size }));
    toggleFab(false);
    fetchManufacturers();
  }, []);

  // Update subtitle when selection changes
  useEffect(() => {
    updateTitle('', t('main.associateProducts'), t('edit.productsSelected', { count: selectedProducts.size }));
  }, [selectedProducts.size]);

  const fetchManufacturers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}products?for_assoc`,
        { withCredentials: true }
      );

      const stringData = compressedBase64ToString(response.data.data);
      //console.log(stringData);
      const data = JSON.parse(stringData) || [];
      setManufacturers(data);
      
      // Initialize selected products based on is_associate_with_vet flag
      const initialSelected = new Set();
      data.forEach(manufacturer => {
        manufacturer.products?.forEach(product => {
          if (product.is_associate_with_vet === 1) {
            initialSelected.add(product.id);
          }
        });
      });
      setSelectedProducts(initialSelected);
    } catch (error) {
      handleError(error);
      snackbar(t('edit.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductToggle = useCallback((productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const handleManufacturerToggle = useCallback((manufacturer, manufacturerProductIds, allSelected) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all products from this manufacturer
        manufacturerProductIds.forEach(id => newSet.delete(id));
      } else {
        // Select all products from this manufacturer
        manufacturerProductIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  }, []);

  const handleAccordionChange = useCallback((manufacturerId, isExpanded) => {
    setExpandedManufacturers(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(manufacturerId);
      } else {
        newSet.delete(manufacturerId);
      }
      return newSet;
    });
  }, []);

  const handleSubmit = async () => {
    if (selectedProducts.size === 0) {
      snackbar(t('edit.nothingToRegister'));
      return;
    }

    setIsLoading(true);
    try {
      const productsArray = Array.from(selectedProducts);

      await axios.put(
        `${import.meta.env.VITE_API_URL}products?associates`,
        { products: productsArray },
        { withCredentials: true }
      );

      snackbar(t('edit.productsAssociatedSuccess'));
      navigate(-1);
    } catch (error) {
      handleError(error);
      snackbar(t('common.errorSaving'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      {/* Manufacturers List */}
      <Paper sx={{ mb: 3, overflowX: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {manufacturers.map((manufacturer, index) => (
          <ManufacturerAccordion
            key={manufacturer.id}
            manufacturer={manufacturer}
            isExpanded={expandedManufacturers.has(manufacturer.id)}
            selectedProducts={selectedProducts}
            onAccordionChange={handleAccordionChange}
            onManufacturerToggle={handleManufacturerToggle}
            onProductToggle={handleProductToggle}
            isLastItem={index === manufacturers.length - 1}
          />
        ))}

        {manufacturers.length === 0 && !isLoading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('edit.noManufacturersAvailable')}
            </Typography>
          </Box>
        )}
      </Paper>
    </EditPage>
  );
};

export default EditProductAssoc;
